/**
 * Save Sync Manager
 * Manages automatic cloud save synchronization for the emulator
 * Handles periodic auto-saves and manual save operations
 */

import { EmulatorCore } from '../emulator/EmulatorCore';
import { cloudSaveService } from './CloudSaveService';
import { localSaveService } from './LocalSaveService';

export interface SaveSyncConfig {
  romHash: string;
  romName: string;
  saveSlot?: number;
  autoSaveInterval?: number; // in milliseconds, default 5 minutes
  enableCloudSync?: boolean;
}

export class SaveSyncManager {
  private emulatorCore: EmulatorCore;
  private config: SaveSyncConfig;
  private autoSaveTimer: NodeJS.Timeout | null = null;
  private isEnabled = false;
  private lastSaveTime = 0;

  constructor(emulatorCore: EmulatorCore, config: SaveSyncConfig) {
    this.emulatorCore = emulatorCore;
    this.config = {
      saveSlot: 0,
      autoSaveInterval: 5 * 60 * 1000, // 5 minutes
      enableCloudSync: true,
      ...config,
    };
  }

  /**
   * Initialize and load cloud save if available
   */
  async initialize(): Promise<void> {
    console.log('🔄 Initializing save sync manager...');

    try {
      // Try to load cloud save
      if (this.config.enableCloudSync) {
        const cloudSave = await cloudSaveService.getSaveByRomHash(
          this.config.romHash,
          this.config.saveSlot
        );

        if (cloudSave) {
          console.log('☁️ Cloud save found, downloading...');
          const saveData = await cloudSaveService.downloadSave(cloudSave.id);

          // Load into emulator
          this.emulatorCore.setSaveRAM(saveData);

          // Save to local storage as backup
          await localSaveService.putSave({
            romHash: this.config.romHash,
            romName: this.config.romName,
            saveSlot: this.config.saveSlot!,
            saveData,
            timestamp: new Date(cloudSave.updatedAt).getTime(),
          });

          console.log('✅ Cloud save loaded successfully');
        } else {
          // Try local save
          const localSave = await localSaveService.getSave(
            this.config.romHash,
            this.config.saveSlot
          );

          if (localSave) {
            console.log('💾 Local save found, loading...');
            this.emulatorCore.setSaveRAM(localSave.saveData);
            console.log('✅ Local save loaded successfully');
          } else {
            console.log('ℹ️ No existing saves found (starting fresh)');
          }
        }
      }
    } catch (error) {
      console.error('⚠️ Failed to load save:', error);
    }
  }

  /**
   * Start automatic save synchronization
   */
  start(): void {
    if (this.isEnabled) {
      console.warn('Save sync already started');
      return;
    }

    this.isEnabled = true;
    console.log(`✅ Auto-save enabled (every ${this.config.autoSaveInterval! / 60000} minutes)`);

    // Start auto-save timer
    this.autoSaveTimer = setInterval(() => {
      this.autoSave();
    }, this.config.autoSaveInterval);
  }

  /**
   * Stop automatic save synchronization
   */
  stop(): void {
    if (!this.isEnabled) {
      return;
    }

    this.isEnabled = false;

    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    console.log('⏹️ Auto-save stopped');
  }

  /**
   * Perform auto-save (internal method)
   */
  private async autoSave(): Promise<void> {
    try {
      // Extract save data from emulator
      const saveData = this.emulatorCore.getSaveRAM();

      if (!saveData || saveData.length === 0) {
        console.log('ℹ️ No save data to sync');
        return;
      }

      const now = Date.now();

      // Save locally first (fast)
      await localSaveService.putSave({
        romHash: this.config.romHash,
        romName: this.config.romName,
        saveSlot: this.config.saveSlot!,
        saveData,
        timestamp: now,
      });

      // Upload to cloud if enabled
      if (this.config.enableCloudSync) {
        await cloudSaveService.uploadSave(
          this.config.romHash,
          saveData,
          this.config.romName,
          this.config.saveSlot
        );
        console.log('☁️ Auto-save synced to cloud');
      } else {
        console.log('💾 Auto-save completed (local only)');
      }

      this.lastSaveTime = now;
    } catch (error) {
      console.error('⚠️ Auto-save failed:', error);
    }
  }

  /**
   * Manually save to cloud
   */
  async saveToCloud(): Promise<void> {
    try {
      console.log('💾 Manual save to cloud...');

      const saveData = this.emulatorCore.getSaveRAM();

      if (!saveData || saveData.length === 0) {
        throw new Error('No save data available');
      }

      const now = Date.now();

      // Save locally
      await localSaveService.putSave({
        romHash: this.config.romHash,
        romName: this.config.romName,
        saveSlot: this.config.saveSlot!,
        saveData,
        timestamp: now,
      });

      // Upload to cloud
      await cloudSaveService.uploadSave(
        this.config.romHash,
        saveData,
        this.config.romName,
        this.config.saveSlot
      );

      this.lastSaveTime = now;
      console.log('✅ Save uploaded to cloud successfully');
    } catch (error) {
      console.error('❌ Failed to save to cloud:', error);
      throw error;
    }
  }

  /**
   * Get time since last save
   */
  getTimeSinceLastSave(): number {
    if (this.lastSaveTime === 0) {
      return Infinity;
    }
    return Date.now() - this.lastSaveTime;
  }

  /**
   * Get last save time as Date
   */
  getLastSaveTime(): Date | null {
    if (this.lastSaveTime === 0) {
      return null;
    }
    return new Date(this.lastSaveTime);
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<SaveSyncConfig>): void {
    this.config = { ...this.config, ...config };

    // Restart auto-save with new interval if running
    if (this.isEnabled && config.autoSaveInterval) {
      this.stop();
      this.start();
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.stop();
  }
}

/**
 * Create and configure a SaveSyncManager instance
 */
export function createSaveSyncManager(
  emulatorCore: EmulatorCore,
  config: SaveSyncConfig
): SaveSyncManager {
  return new SaveSyncManager(emulatorCore, config);
}
